import React from 'react';
import heic2any from 'heic2any';
import { FormInstance, Upload as UploadAntd } from 'antd';
import { RcFile } from 'antd/es/upload';

// Utility function to extract the actual File object from various structures
export const extractFileObject = (fileValue: any): File | null => {
  if (!fileValue) return null;

  // If it's already a File object
  if (fileValue instanceof File) {
    return fileValue;
  }

  // If it's an object with originFileObj (from antd Upload)
  if (fileValue && typeof fileValue === 'object' && 'originFileObj' in fileValue) {
    return fileValue.originFileObj;
  }

  // If it's an object with fileList (from antd Upload)
  if (fileValue && typeof fileValue === 'object' && 'fileList' in fileValue) {
    const { fileList } = fileValue;
    if (fileList && fileList.length > 0) {
      const firstFile = fileList[0];
      return firstFile.originFileObj || firstFile;
    }
  }

  // If it's an object that might be a File-like object from ImgCrop
  if (fileValue && typeof fileValue === 'object' && 'name' in fileValue && 'size' in fileValue) {
    // This might be a File-like object from ImgCrop
    return fileValue as File;
  }

  return null;
};

function readAsDataURL(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Error'));
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Error'));
      }
    };
    reader.readAsDataURL(file);
  });
}

async function heicToJpeg(file: File) {
  const dataURL = await readAsDataURL(file);

  let jpegBlob;
  try {
    const originalBlob = await fetch(dataURL).then((res) => res.blob());

    let quality = 0.7;

    while (true) {
      // eslint-disable-next-line no-await-in-loop
      jpegBlob = (await heic2any({
        blob: originalBlob,
        toType: 'image/jpeg',
        quality,
      })) as Blob;

      if (jpegBlob.size <= 1024 * 1024 * 5 || quality <= 0.4) break;

      quality -= 0.2;
    }
  } catch (err) {
    const bin = atob(dataURL.split(',')[1]);
    const buffer = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) buffer[i] = bin.charCodeAt(i);
    jpegBlob = new Blob([buffer], { type: file.type });
  }

  const fileName = file.name.replace(/\.[^.]+$/, '.jpg');
  const jpgFile = new File([jpegBlob], fileName, { type: 'image/jpeg' });

  return jpgFile as RcFile;
}

export function makeSquare(file: File): Promise<RcFile> {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      if (img.width === img.height) resolve(file as RcFile);
      const size = Math.max(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      const dx = (size - img.width) / 2;
      const dy = (size - img.height) / 2;
      ctx.drawImage(img, dx, dy, img.width, img.height);

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(new File([blob], file.name, { type: file.type }) as RcFile);
        }
      }, file.type);
    };

    reader.readAsDataURL(file);
  });
}

export const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

export const checkImageDimensions = async (file: File, isEs: boolean): Promise<void> => {
  let url: string;
  let isObjectUrl = false;

  if (file.type === 'image/heic') {
    url = await getBase64(file);
  } else {
    url = URL.createObjectURL(file);
    isObjectUrl = true;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (isObjectUrl) URL.revokeObjectURL(url);
      if (img.width >= 256 && img.height >= 256) {
        resolve();
      } else {
        reject(new Error(isEs ? 'La resolución mínima es de 256×256px' : 'Min resolution is 256×256px'));
      }
    };
    img.onerror = () => {
      if (isObjectUrl) URL.revokeObjectURL(url);
      reject(new Error(isEs ? 'Error al subir' : 'Upload error'));
    };
    img.src = url;
  });
};

export const beforeUpload =
  (
    form: FormInstance<any>,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    name: string,
    isEs: boolean,
    isUpload?: boolean,
  ) =>
  async (f: RcFile) => {
    let file = f;
    const validTypes = ['image/png', 'image/jpg', 'image/jpeg', ''];

    setLoading(true);

    try {
      if (file.type === 'image/heic' || file.name.endsWith('.heic') || file.name.endsWith('.HEIC')) {
        file = await heicToJpeg(file);
      }
    } catch (err) {
      setError(isEs ? 'Archivo no válido' : 'Invalid file');
      setLoading(false);
      return isUpload ? UploadAntd.LIST_IGNORE : false;
    }

    if (!isUpload) file = await makeSquare(file);

    if (!validTypes.includes(file.type)) {
      setError(`${isEs ? 'Formatos permitidos' : 'Allowed formats'}: .png, .jpg, .jpeg, .heic`);
      setLoading(false);
      return isUpload ? UploadAntd.LIST_IGNORE : false;
    }

    if (file.size / 1024 / 1024 > 10) {
      const error = `${isEs ? 'Tamaño máximo de imagen' : 'Max image size'}: 10MB`;
      form.setFields([{ name, errors: [error] }]);
      setError(error);
      setLoading(false);
      return isUpload ? UploadAntd.LIST_IGNORE : false;
    }

    try {
      await checkImageDimensions(file, isEs);
    } catch (err) {
      if (err instanceof Error) setError(err.message);
      setLoading(false);
      return isUpload ? UploadAntd.LIST_IGNORE : false;
    }

    setError(null);
    setLoading(false);
    return file;
  };
